// Silent Proof-of-Work solver for the OTP send endpoints (register / resend).
//
// Fetches a challenge from the API, brute-forces a nonce so that
// sha256("<challenge>.<nonce>") starts with N hex zeros, and returns the
// fields to merge into the request body. Fully invisible to the user — it just
// burns a fraction of a second of CPU. Self-contained sha256 (no dependency).

// --- SHA-256 (canonical compact implementation by Geraint Luff, public domain).
// Returns lowercase hex. Verified against the standard "abc" test vector and the
// PHP hash('sha256', ...) output used by the server. ASCII input only (our
// inputs are hex + dots + digits).
function sha256Hex(ascii) {
  function rightRotate(value, amount) {
    return (value >>> amount) | (value << (32 - amount));
  }

  const mathPow = Math.pow;
  const maxWord = mathPow(2, 32);
  let i, j;
  let result = "";

  const words = [];
  const asciiBitLength = ascii.length * 8;

  let hash = (sha256Hex.h = sha256Hex.h || []);
  const k = (sha256Hex.k = sha256Hex.k || []);
  let primeCounter = k.length;

  const isComposite = {};
  for (let candidate = 2; primeCounter < 64; candidate++) {
    if (!isComposite[candidate]) {
      for (i = 0; i < 313; i += candidate) {
        isComposite[i] = candidate;
      }
      hash[primeCounter] = (mathPow(candidate, 0.5) * maxWord) | 0;
      k[primeCounter++] = (mathPow(candidate, 1 / 3) * maxWord) | 0;
    }
  }

  ascii += "\x80";
  while ((ascii.length % 64) - 56) ascii += "\x00";
  for (i = 0; i < ascii.length; i++) {
    j = ascii.charCodeAt(i);
    if (j >> 8) return "";
    words[i >> 2] |= j << (((3 - i) % 4) * 8);
  }
  words[words.length] = (asciiBitLength / maxWord) | 0;
  words[words.length] = asciiBitLength;

  for (j = 0; j < words.length; ) {
    const w = words.slice(j, (j += 16));
    const oldHash = hash;
    hash = hash.slice(0, 8);

    for (i = 0; i < 64; i++) {
      const w15 = w[i - 15];
      const w2 = w[i - 2];

      const a = hash[0];
      const e = hash[4];
      const temp1 =
        hash[7] +
        (rightRotate(e, 6) ^ rightRotate(e, 11) ^ rightRotate(e, 25)) +
        ((e & hash[5]) ^ (~e & hash[6])) +
        k[i] +
        (w[i] =
          i < 16
            ? w[i]
            : (w[i - 16] +
                (rightRotate(w15, 7) ^ rightRotate(w15, 18) ^ (w15 >>> 3)) +
                w[i - 7] +
                (rightRotate(w2, 17) ^ rightRotate(w2, 19) ^ (w2 >>> 10))) |
              0);

      const temp2 =
        (rightRotate(a, 2) ^ rightRotate(a, 13) ^ rightRotate(a, 22)) +
        ((a & hash[1]) ^ (a & hash[2]) ^ (hash[1] & hash[2]));

      hash = [(temp1 + temp2) | 0].concat(hash);
      hash[4] = (hash[4] + temp1) | 0;
    }

    for (i = 0; i < 8; i++) {
      hash[i] = (hash[i] + oldHash[i]) | 0;
    }
  }

  for (i = 0; i < 8; i++) {
    for (j = 3; j + 1; j--) {
      const b = (hash[i] >> (j * 8)) & 255;
      result += (b < 16 ? "0" : "") + b.toString(16);
    }
  }
  return result;
}

// Solve the challenge. apiBase defaults to same-origin /api.
export async function solvePow(apiBase) {
  const base = apiBase || `${window.location.origin}/api`;
  try {
    const res = await fetch(`${base}/pow-challenge`, {
      headers: { Accept: "application/json" },
    });
    const json = await res.json();
    const data = json && json.data ? json.data : json;
    const challenge = data.challenge;
    const sig = data.sig;
    const difficulty = data.difficulty || 4;
    if (!challenge || !sig) return {};

    const prefix = "0".repeat(difficulty);
    let nonce = 0;
    // difficulty 4 ≈ ~65k hashes ≈ well under a second
    while (!sha256Hex(`${challenge}.${nonce}`).startsWith(prefix)) {
      nonce++;
    }
    return { pow_challenge: challenge, pow_sig: sig, pow_nonce: nonce };
  } catch (e) {
    // Network/parse failure: return nothing. If PoW is enabled server-side the
    // request will be rejected and the user can retry; we never block the UI.
    return {};
  }
}

export const pow = { solvePow, sha256Hex };
